import React, { ReactNode, useContext } from 'react'
import { TableCell, TableSortLabel, Typography } from '@mui/material'
import { OverflowTooltipWrapper } from '../../OverflowTooltipWrapper/OverflowTooltipWrapper'
import { DataTableBackgroundContext } from '../DataTableBackground'
import { getStickyCellSx, StickyCell, withoutSeam } from '../DataTable.sticky'
import { DataTableAlign, SortDirection } from '../DataTable.types'
import { DataTableTypographyVariant } from '../DataTable.variants'

type DataTableHeaderCellProps = {
    children?: ReactNode
    tooltip?: string
    width: string
    align?: DataTableAlign
    paddingX?: number
    typographyVariant?: DataTableTypographyVariant
    isSortable?: boolean
    isSortActive?: boolean
    sortDirection?: SortDirection
    onSort?: () => void
    sticky?: StickyCell
}

const SORT_ARIA_LABELS: Record<SortDirection, 'ascending' | 'descending'> = {
    asc: 'ascending',
    desc: 'descending',
}

const SORT_ICON_WIDTH = 26

/** The arrow keeps its 18px box plus 4px margins even unsorted, so it is balanced, not ignored. */
const SORT_LABEL_ALIGNMENT: Partial<Record<DataTableAlign, object>> = {
    center: { pl: `${SORT_ICON_WIDTH}px` },
    right: { flexDirection: 'row-reverse' },
}
export const DataTableHeaderCell = ({
    children,
    tooltip,
    width,
    align = 'left',
    paddingX = 2,
    typographyVariant = '18x600',
    isSortable = false,
    isSortActive = false,
    sortDirection = 'asc',
    onSort,
    sticky,
}: DataTableHeaderCellProps) => {
    const backdrop = useContext(DataTableBackgroundContext)

    const label = (
        <OverflowTooltipWrapper title={tooltip || children} placement="top-start" isAlwaysShown={!!tooltip}>
            <Typography variant={typographyVariant} color="inherit" sx={{ minWidth: 0 }}>
                {children}
            </Typography>
        </OverflowTooltipWrapper>
    )

    return (
        <TableCell
            align={align}
            aria-sort={isSortable ? (isSortActive ? SORT_ARIA_LABELS[sortDirection] : 'none') : undefined}
            sx={[
                {
                    width,
                    px: paddingX,
                    pt: 0,
                    pb: 1.5,
                    borderBottom: 'none',
                    color: 'figma.grayscale.textHier1',
                    overflow: 'hidden',
                },
                getStickyCellSx(withoutSeam(sticky), backdrop),
            ]}
        >
            {isSortable && onSort ? (
                <TableSortLabel
                    active={isSortActive}
                    direction={sortDirection}
                    onClick={onSort}
                    hideSortIcon={false}
                    sx={{
                        color: 'inherit',
                        maxWidth: '100%',
                        ...SORT_LABEL_ALIGNMENT[align],
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important',
                            flexShrink: 0,
                        },
                        '&:hover': {
                            color: 'figma.primary',
                        },
                        '&.Mui-active': {
                            color: 'figma.primary',
                        },
                    }}
                >
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    )
}

export default DataTableHeaderCell
